import ResearchFindingStore from "components/Admin/Organizations/OrganizationDetail/OrganizationResearchFindings/ResearchFindingStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import type { EmailSuggestion } from "components/UI/AcxEmailChipInput";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import type { ResearchRunSummary } from "models/ResearchFindings/ResearchFindings";
import ResearchFindingsService from "services/ResearchFindingsService";
import { UserService } from "services/UserService";
import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "./RootStore";
import type { IRootStore } from "./RootStore";

const MACHINE_USER_EMAIL = "acxmachine@authenticx.com";

export type ResearchFindingsInitialState = {
    page?: number;
    runId?: string;
};

/**
 * Singleton store for the Research Findings page. Owns the lightweight run list and a Map of
 * per-run ResearchFindingStore instances (Client Brain-style composition). Run detail is large
 * (~1.5 MB RunOutput), so it is NOT loaded up front — a per-run store is instantiated and its
 * detail fetched only when the run is first selected, then cached in the map.
 */
@AcxStore
export class ResearchFindingsStore extends BaseStore {
    private readonly service = new ResearchFindingsService();
    private readonly userService = new UserService();

    static readonly RUNS_PAGE_SIZE = 10;

    /** Current page of run summaries (server-side paginated). */
    @observable runs: ResearchRunSummary[] = [];
    @observable runStores: Map<string, ResearchFindingStore> = new Map();
    @observable selectedRunId?: string;
    @observable isLoading = false;
    /** 1-based page for the run list. */
    @observable runsPage = 1;
    @observable totalRunCount = 0;

    // Org users offered as recipient suggestions in the email dialog. Loaded once per org.
    @observable emailSuggestions: EmailSuggestion[] = [];
    @observable isEmailSuggestionsLoading = false;

    private orgId?: string;

    constructor(private rootStore: IRootStore) {
        super("ResearchFindingsStore");
        makeObservable(this);
    }

    @computed
    get selectedRunStore(): ResearchFindingStore | undefined {
        return this.selectedRunId
            ? this.runStores.get(this.selectedRunId)
            : undefined;
    }

    @computed
    get runsPageCount(): number {
        return Math.max(
            1,
            Math.ceil(this.totalRunCount / ResearchFindingsStore.RUNS_PAGE_SIZE),
        );
    }

    @action
    async loadRuns(orgId: string, initialState?: ResearchFindingsInitialState) {
        this.orgId = orgId;
        void this.loadEmailSuggestions(orgId);
        await this.fetchPage(initialState?.page ?? 1, !initialState?.runId);
        if (initialState?.runId) {
            this.selectRun(initialState.runId);
        }
    }

    /**
     * Loads the org's users once as email-recipient suggestions for the send dialog. Failure
     * tolerant — suggestions are a convenience, typing addresses still works without them.
     */
    @action
    private async loadEmailSuggestions(orgId: string) {
        this.isEmailSuggestionsLoading = true;
        try {
            const users = await this.userService.getUsersForSharing(orgId);
            runInAction(() => {
                this.emailSuggestions = users
                    .filter((u) => u.email && u.email !== MACHINE_USER_EMAIL)
                    .map((u) => ({
                        email: u.email,
                        name: `${u.firstName} ${u.lastName}`.trim(),
                    }));
            });
        } catch {
            runInAction(() => {
                this.emailSuggestions = [];
            });
        } finally {
            runInAction(() => {
                this.isEmailSuggestionsLoading = false;
            });
        }
    }

    @action
    async setRunsPage(page: number) {
        await this.fetchPage(page, false);
    }

    @action
    async refreshCurrentPage() {
        const selectedRunId = this.selectedRunId;
        await this.fetchPage(this.runsPage, false);
        if (selectedRunId) {
            this.runStores.delete(selectedRunId);
            this.selectRun(selectedRunId);
        }
    }

    @action
    clearSelectedRun() {
        this.selectedRunId = undefined;
    }

    /** Fetches one server-side page of runs. */
    @action
    private async fetchPage(page: number, autoSelectFirst: boolean) {
        if (!this.orgId) return;
        this.isLoading = true;
        try {
            const result = await this.service.getRuns(
                page,
                ResearchFindingsStore.RUNS_PAGE_SIZE,
            );
            runInAction(() => {
                this.runs = result.items;
                this.totalRunCount = result.totalCount;
                this.runsPage = result.page;
                // Auto-select the first run so the detail pane isn't empty on load.
                if (
                    autoSelectFirst &&
                    !this.selectedRunId &&
                    result.items.length > 0
                ) {
                    this.selectRun(result.items[0].id);
                }
            });
        } catch (err: unknown) {
            this.rootStore
                .getStore(MessageStore)
                .logError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load research runs",
                );
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    /** Selects a run, lazily creating + loading its detail store on first access. */
    @action
    selectRun(runId: string) {
        this.selectedRunId = runId;
        let store = this.runStores.get(runId);
        if (!store) {
            store = new ResearchFindingStore(runId, this.rootStore, this.service);
            this.runStores.set(runId, store);
            void store.load();
        }
    }

    @action
    resetState() {
        this.runs = [];
        this.runStores = new Map();
        this.selectedRunId = undefined;
        this.isLoading = false;
        this.runsPage = 1;
        this.totalRunCount = 0;
        this.emailSuggestions = [];
        this.isEmailSuggestionsLoading = false;
        this.orgId = undefined;
    }
}

export default ResearchFindingsStore;
